import {
	FiChevronDown,
	FiChevronUp,
	FiMenu,
	FiX,
} from "react-icons/fi";

import { cn } from "utils/helpers";
import type { LocalColumn, ScheduleColumnType } from "../schedule-of-rates.types";
import { COLUMN_TYPE_OPTIONS } from "../schedule-utils";

export interface ScheduleColumnConfigItemProps {
	column: LocalColumn;
	dragHandleProps: Record<string, unknown> | null | undefined;
	index: number;
	isFirst: boolean;
	isLast: boolean;
	isOnlyColumn: boolean;
	onLabelChange: (localId: string, label: string) => void;
	onMoveColumn: (localId: string, direction: "up" | "down") => void;
	onRemoveColumn: (localId: string) => void;
	onTypeChange: (localId: string, type: ScheduleColumnType) => void;
}

const ScheduleColumnConfigItem: React.FC<ScheduleColumnConfigItemProps> = ({
	column,
	dragHandleProps,
	index,
	isFirst,
	isLast,
	isOnlyColumn,
	onLabelChange,
	onMoveColumn,
	onRemoveColumn,
	onTypeChange,
}) => {
	return (
		<div
			className="flex items-center gap-2 rounded border border-gray-200 bg-white px-2 py-1.5"
			data-testid={`schedule-column-row-${index}`}
		>
			<div
				{...dragHandleProps}
				className="cursor-grab text-gray-400"
			>
				<FiMenu size={14} />
			</div>

			<input
				aria-label={`Column ${index + 1} name`}
				className="flex-1 rounded border border-gray-300 px-2 py-1 text-sm"
				data-testid={`schedule-column-name-${index}`}
				onChange={(e) => onLabelChange(column.localId, e.target.value)}
				value={column.label}
			/>

			<select
				aria-label={`Column ${index + 1} type`}
				className="w-[200px] rounded border border-gray-300 bg-white px-2 py-1 text-sm"
				data-testid={`schedule-column-type-${index}`}
				onChange={(e) =>
					onTypeChange(
						column.localId,
						e.target.value as ScheduleColumnType
					)
				}
				value={column.columnType}
			>
				{COLUMN_TYPE_OPTIONS.map((opt) => (
					<option key={opt.value} value={opt.value}>
						{opt.label}
					</option>
				))}
			</select>

			<div className="flex flex-col">
				<button
					aria-label={`Move column ${index + 1} up`}
					className="text-gray-400 hover:text-gray-600 disabled:opacity-30"
					disabled={isFirst}
					onClick={() => onMoveColumn(column.localId, "up")}
					type="button"
				>
					<FiChevronUp size={12} />
				</button>
				<button
					aria-label={`Move column ${index + 1} down`}
					className="text-gray-400 hover:text-gray-600 disabled:opacity-30"
					disabled={isLast}
					onClick={() => onMoveColumn(column.localId, "down")}
					type="button"
				>
					<FiChevronDown size={12} />
				</button>
			</div>

			<button
				aria-label={`Remove column ${column.label}`}
				className={cn(
					"text-gray-400 hover:text-red-500",
					isOnlyColumn && "cursor-not-allowed opacity-30"
				)}
				data-testid={`schedule-remove-column-${index}`}
				disabled={isOnlyColumn}
				onClick={() => onRemoveColumn(column.localId)}
				type="button"
			>
				<FiX size={14} />
			</button>
		</div>
	);
};

export { ScheduleColumnConfigItem };
